import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { trackBasecampEvent } from "../analytics";
import Shell from "../components/Shell";
import Spinner from "../components/Spinner";
import {
  useVerifyBasecampEmailMutation,
  type VerifyBasecampEmailStatus,
} from "../generated/graphql";

const VerifyEmailPage = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") ?? "";
  const [status, setStatus] = useState<
    VerifyBasecampEmailStatus | "LOADING" | "INVALID"
  >("LOADING");
  const [firstName, setFirstName] = useState<string | null>(null);
  const [verify] = useVerifyBasecampEmailMutation();

  useEffect(() => {
    if (!token) {
      setStatus("INVALID");
      trackBasecampEvent("basecamp_email_verification_failed", {
        errorCategory: "validation",
        flow: "email_verification",
        outcome: "failure",
        surface: "web",
        verificationStatus: "INVALID",
      });
      return;
    }
    void (async () => {
      try {
        const { data } = await verify({
          variables: { verificationToken: token },
        });
        const result = data?.verifyBasecampEmail;
        const nextStatus = result?.status ?? "INVALID";
        setStatus(nextStatus);
        setFirstName(result?.guestFirstName ?? null);
        if (nextStatus === "VERIFIED") {
          trackBasecampEvent("basecamp_email_verification_completed", {
            flow: "email_verification",
            outcome: "success",
            surface: "web",
            verificationStatus: nextStatus,
          });
        } else {
          trackBasecampEvent("basecamp_email_verification_failed", {
            errorCategory: "verification_failed",
            flow: "email_verification",
            outcome: "failure",
            surface: "web",
            verificationStatus: nextStatus,
          });
        }
      } catch {
        setStatus("INVALID");
        trackBasecampEvent("basecamp_email_verification_failed", {
          errorCategory: "verification_failed",
          flow: "email_verification",
          outcome: "failure",
          surface: "web",
          verificationStatus: "INVALID",
        });
      }
    })();
    // verify intentionally not in deps — fires once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const goHome = () => navigate("/");

  return (
    <Shell>
      <main className="relative z-10 flex flex-1 flex-col items-center justify-center px-6 pb-12">
        <div className="bc-fade-up w-full max-w-sm space-y-6 text-center">
          {status === "LOADING" && <Spinner className="size-6 text-white/30" />}

          {status === "VERIFIED" && (
            <>
              <h1 className="font-oswald text-3xl font-medium uppercase tracking-wide text-white">
                Email Confirmed{firstName ? `, ${firstName}` : ""}
              </h1>
              <p className="text-sm text-white/60">
                Thanks — we&apos;ll be able to reach you about your stay.
              </p>
            </>
          )}

          {status === "EXPIRED" && (
            <>
              <h1 className="font-oswald text-2xl font-medium uppercase tracking-wide text-white">
                Link expired
              </h1>
              <p className="text-sm text-white/60">
                Open Basecamp to send a new one.
              </p>
            </>
          )}

          {status === "INVALID" && (
            <>
              <h1 className="font-oswald text-2xl font-medium uppercase tracking-wide text-white">
                We couldn&apos;t confirm this link
              </h1>
              <p className="text-sm text-white/60">
                Open Basecamp to try again.
              </p>
            </>
          )}

          {status !== "LOADING" && (
            <button
              type="button"
              onClick={goHome}
              className="w-full rounded-xl bg-accentGreen py-3 font-oswald text-sm font-medium uppercase tracking-wider text-primaryDarkBlue"
            >
              Open Basecamp
            </button>
          )}
        </div>
      </main>
    </Shell>
  );
};

export default VerifyEmailPage;
